import React, { useState, useEffect } from 'react';
import { Card, Collapse, Form, Input, Button, message, Spin, Typography, Space, Select } from 'antd';
import { SaveOutlined, PlusOutlined, MinusCircleOutlined, GlobalOutlined } from '@ant-design/icons';
import { getGenericConfig, updateGenericConfig } from '../api';

const { Title, Text } = Typography;
const { Panel } = Collapse;

// Common IANA timezones. The value is what the backend stores under the
// 'TIMEZONE' config key and uses to compute business dates (see utils/businessTime.js).
const TIMEZONE_OPTIONS = [
  { value: 'Asia/Kolkata', label: '(GMT+05:30) India Standard Time — Asia/Kolkata' },
  { value: 'Asia/Dubai', label: '(GMT+04:00) Gulf Standard Time — Asia/Dubai' },
  { value: 'Asia/Karachi', label: '(GMT+05:00) Pakistan Standard Time — Asia/Karachi' },
  { value: 'Asia/Dhaka', label: '(GMT+06:00) Bangladesh Standard Time — Asia/Dhaka' },
  { value: 'Asia/Kathmandu', label: '(GMT+05:45) Nepal Time — Asia/Kathmandu' },
  { value: 'Asia/Colombo', label: '(GMT+05:30) Sri Lanka Time — Asia/Colombo' },
  { value: 'Asia/Singapore', label: '(GMT+08:00) Singapore Time — Asia/Singapore' },
  { value: 'Europe/London', label: '(GMT+00:00) Greenwich Mean Time — Europe/London' },
  { value: 'America/New_York', label: '(GMT-05:00) Eastern Time — America/New_York' },
  { value: 'UTC', label: '(GMT+00:00) Coordinated Universal Time — UTC' },
];

const DEFAULT_TIMEZONE = 'Asia/Kolkata';

const DynamicTermsList = ({ name, label }) => (
    <div style={{ marginBottom: 24 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>{label}</Text>
        <Form.List name={name}>
            {(fields, { add, remove }) => (
                <>
                    {fields.map((field, index) => (
                        <Space key={field.key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                            <Text strong>{index + 1}.</Text>
                            <Form.Item
                                {...field}
                                style={{ margin: 0, width: '100%' }}
                            >
                                <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} style={{ width: 600 }} />
                            </Form.Item>
                            <MinusCircleOutlined onClick={() => remove(field.name)} style={{ color: 'red' }} />
                        </Space>
                    ))}
                    <Form.Item>
                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={{ width: 600, marginTop: 8 }}>
                            Add Term
                        </Button>
                    </Form.Item>
                </>
            )}
        </Form.List>
    </div>
);

const GeneralSettings = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // Timezone setting
    const [timezone, setTimezone] = useState(DEFAULT_TIMEZONE);
    const [tzLoading, setTzLoading] = useState(false);
    const [tzSaving, setTzSaving] = useState(false);

    const CONFIG_KEY = 'TERMS_AND_CONDITIONS';
    const TIMEZONE_KEY = 'TIMEZONE';

    useEffect(() => {
        fetchConfig();
        fetchTimezone();
    }, []);

    const fetchTimezone = async () => {
        try {
            setTzLoading(true);
            const data = await getGenericConfig(TIMEZONE_KEY);
            const val = data && data.value;
            const tz = typeof val === 'string' ? val : (val && val.timezone);
            if (tz) setTimezone(tz);
        } catch (error) {
            // Non-fatal — fall back to default
        } finally {
            setTzLoading(false);
        }
    };

    const handleSaveTimezone = async () => {
        try {
            setTzSaving(true);
            await updateGenericConfig(TIMEZONE_KEY, { timezone });
            message.success('Time zone saved. Business dates now use this zone.');
        } catch (error) {
            message.error('Failed to save time zone');
        } finally {
            setTzSaving(false);
        }
    };

    const fetchConfig = async () => {
        try {
            setLoading(true);
            const data = await getGenericConfig(CONFIG_KEY);
            if (data && data.value) {
                // Backward compatibility mapping for old \n-separated strings
                const mappedValue = {};
                const keys = [
                    'pledgeCaseAgreement', 
                    'pledgeCasePurchaseBill', 
                    'walkinCasePurchaseBill', 
                    'walkinCaseAgreement'
                ];
                
                keys.forEach(key => {
                    const val = data.value[key];
                    if (typeof val === 'string') {
                        mappedValue[key] = val.split('\n').filter(t => t.trim() !== '');
                    } else if (Array.isArray(val)) {
                        mappedValue[key] = val;
                    } else {
                        mappedValue[key] = [];
                    }
                });

                form.setFieldsValue(mappedValue);
            }
        } catch (error) {
            message.error('Failed to load terms and conditions');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (values) => {
        try {
            setSaving(true);
            // Values are already arrays of strings due to Form.List
            await updateGenericConfig(CONFIG_KEY, values);
            message.success('Terms and conditions saved successfully');
        } catch (error) {
            message.error('Failed to save terms and conditions');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ padding: '24px' }}>
            <Card
                title={<Space><GlobalOutlined /> Time Zone</Space>}
                bordered={false}
                style={{ marginBottom: 24 }}
            >
                <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                    Controls how business dates (e.g. End of Day, cash ledger, treasury reports) are
                    computed across the system. Changing this affects which calendar day new records
                    are attributed to.
                </Text>
                {tzLoading ? (
                    <Spin />
                ) : (
                    <Space wrap>
                        <Select
                            value={timezone}
                            onChange={setTimezone}
                            options={TIMEZONE_OPTIONS}
                            showSearch
                            optionFilterProp="label"
                            style={{ minWidth: 380 }}
                        />
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            loading={tzSaving}
                            onClick={handleSaveTimezone}
                        >
                            Save Time Zone
                        </Button>
                    </Space>
                )}
            </Card>

            <Card title="General Settings" bordered={false}>
                <Title level={4}>Set Terms & Conditions</Title>
                <Text type="secondary" style={{ display: 'block', marginBottom: '24px' }}>
                    Each row represents a separate numbered clause/point in the generated PDFs. You can add or remove terms dynamically.
                </Text>
                
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin size="large" />
                    </div>
                ) : (
                    <Form 
                        form={form} 
                        layout="vertical" 
                        onFinish={handleSave}
                        initialValues={{
                            pledgeCaseAgreement: [],
                            pledgeCasePurchaseBill: [],
                            walkinCaseAgreement: [],
                            walkinCasePurchaseBill: [],
                        }}
                    >
                        <Collapse defaultActiveKey={['1', '2']} style={{ marginBottom: '24px' }}>
                            <Panel header="Pledge Case" key="1">
                                <DynamicTermsList 
                                    name="pledgeCaseAgreement" 
                                    label="Agreement Terms & Conditions" 
                                />
                                <DynamicTermsList 
                                    name="pledgeCasePurchaseBill" 
                                    label="Purchase Bill Terms & Conditions" 
                                />
                            </Panel>
                            
                            <Panel header="Walk-in Case" key="2">
                                <DynamicTermsList 
                                    name="walkinCaseAgreement" 
                                    label="Agreement Terms & Conditions" 
                                />
                                <DynamicTermsList 
                                    name="walkinCasePurchaseBill" 
                                    label="Purchase Bill Terms & Conditions" 
                                />
                            </Panel>
                        </Collapse>
                        
                        <Form.Item>
                            <Button 
                                type="primary" 
                                htmlType="submit" 
                                icon={<SaveOutlined />} 
                                loading={saving}
                            >
                                Save Changes
                            </Button>
                        </Form.Item>
                    </Form>
                )}
            </Card>
        </div>
    );
};

export default GeneralSettings;
